import apiClient from "./middleware";
import { normalizeBannerSwapQueueRequest } from "./normalizeBannerSwapQueueRequest";
import type {
  BannerSwapQueueRequest,
  BannerSwap,
  BannerSwapOverlapAnalysis,
  BannerSwapOverlapResolution,
} from "../utils/@types";

export const bannerSwapApi = {
  postBannerSwap: async (swap: BannerSwapQueueRequest) => {
    try {
      const response = await apiClient.post(
        `/BannerSwap/Queue`,
        normalizeBannerSwapQueueRequest(swap)
      );

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  postChangeDefaultBanner: async (body: {
    channelIDs: string[];
    channelBannerURL: string;
    channelBannerImage: string;
  }) => {
    try {
      const response = await apiClient.post(`/BannerSwap/ChangeDefault`, body);
      return response.data as {
        message: string;
        appliedImmediately: string[];
        deferredUntilSwapEnd: string[];
        failed: Array<{ channelId: string; error: string }>;
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  postBannerSwapOverlapCheck: async (body: {
    channelIDs: string[];
    startDate: Date | string;
    endDate: Date | string;
    excludeSwapUID?: string;
  }): Promise<BannerSwapOverlapAnalysis> => {
    try {
      const response = await apiClient.post(`/BannerSwap/OverlapCheck`, body);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  getBannerSwapQueue: async (): Promise<BannerSwap[]> => {
    try {
      const response = await apiClient.get(`/BannerSwap/Queue`);
      return response.data.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  updateBannerSwapStatus: async (
    bannerSwapId: BannerSwap["swapUID"],
    status: BannerSwap["status"]
  ): Promise<void> => {
    try {
      const response = await apiClient.patch(`/BannerSwap/Status`, {
        bannerSwapId,
        status,
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  endBannerSwapNow: async (
    bannerSwapId: BannerSwap["swapUID"]
  ): Promise<void> => {
    try {
      const response = await apiClient.patch(`/BannerSwap/EndNow`, {
        bannerSwapId,
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  patchBannerSwapRunningEndDate: async (
    bannerSwapId: BannerSwap["swapUID"],
    endDate: Date
  ): Promise<{ message?: string }> => {
    try {
      const response = await apiClient.patch(`/BannerSwap/RunningEndDate`, {
        bannerSwapId,
        endDate: endDate.toISOString(),
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  patchBannerSwapSchedule: async (
    bannerSwapId: BannerSwap["swapUID"],
    startDate: Date,
    endDate: Date,
    overlapResolution?: BannerSwapOverlapResolution
  ): Promise<{ message?: string }> => {
    try {
      const response = await apiClient.patch(`/BannerSwap/Schedule`, {
        bannerSwapId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        ...(overlapResolution ? { overlapResolution } : {}),
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  patchRemoveChannelsFromSwap: async (
    bannerSwapId: BannerSwap["swapUID"],
    channelIdsToRemove: string[]
  ) => {
    try {
      const response = await apiClient.patch(`/BannerSwap/RemoveChannels`, {
        bannerSwapId,
        channelIdsToRemove,
      });
      return response.data as {
        message: string;
        removedChannelIds: string[];
        remainingChannelIds: string[];
        swapCancelled: boolean;
        endTasksEnqueued?: number;
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
};
